<template>
  <div style="background:#fff; padding:20px;">
    <div style="margin-bottom: 20px">
      <a-form layout="inline">
        <a-form-item label="审核进度">
          <a-select
            v-model="query.reviewProgress"
            placeholder="全部"
            allow-clear
            style="width: 150px"
          >
            <a-select-option value="PENDING">待审核</a-select-option>
            <a-select-option value="APPROVED">已通过</a-select-option>
            <a-select-option value="NEED_REVISION">未通过待修改</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="商家ID">
          <a-input-number
            v-model="query.merchantId"
            :min="1"
            placeholder="请输入商家ID"
            style="width: 160px"
          />
        </a-form-item>
        <a-form-item>
          <a-button type="primary" icon="search" @click="handleSearch">查询</a-button>
          <a-button style="margin-left:10px" icon="reload" @click="handleReset">重置</a-button>
        </a-form-item>
      </a-form>
    </div>

    <a-table
      :columns="columns"
      :data-source="data"
      rowKey="id"
      :loading="loading"
      :pagination="pagination"
      @change="handleTableChange"
    >
      <span slot="reviewProgress" slot-scope="text, record">
        <a-tag v-if="record.status === 2" color="volcano">未通过待修改</a-tag>
        <a-tag v-else-if="record.auditStatus === 1" color="green">已通过</a-tag>
        <a-tag v-else color="orange">待审核</a-tag>
      </span>

      <span slot="revisionRemark" slot-scope="text">
        <span v-if="text" :title="text" style="color:#fa541c;">{{ text }}</span>
        <span v-else style="color:#bfbfbf;">-</span>
      </span>

      <span slot="action" slot-scope="text, record">
        <a v-permission="'app:task-audit:list'" style="color:#1890ff" @click="handleOpenDetail(record)">详情</a>
        <template v-if="record.auditStatus === 0 && record.status === 1">
          <a-divider type="vertical" />
          <a v-permission="'app:task-audit:list'" @click="handleOpenEdit(record)">修改</a>
          <a-divider type="vertical" />
          <a-popconfirm title="确定通过该任务?" @confirm="handleApprove(record.id)">
            <a v-permission="'app:task-audit:list'" style="color:#52c41a">通过</a>
          </a-popconfirm>
          <a-divider type="vertical" />
          <a
            v-permission="'app:task-audit:list'"
            style="color:#fa541c"
            @click="handleOpenRevision(record)"
          >未通过</a>
        </template>
      </span>
    </a-table>

    <a-modal
      v-model="detailVisible"
      title="任务详情"
      width="820px"
      :footer="null"
      :destroy-on-close="true"
      @cancel="handleCloseDetail"
    >
      <a-spin :spinning="detailLoading">
        <a-descriptions
          v-if="detail.task"
          bordered
          size="small"
          :column="2"
        >
          <a-descriptions-item label="任务编号">{{ detail.task.taskNo || '-' }}</a-descriptions-item>
          <a-descriptions-item label="商家ID">{{ detail.task.merchantId }}</a-descriptions-item>
          <a-descriptions-item label="标题" :span="2">{{ detail.task.title || '-' }}</a-descriptions-item>
          <a-descriptions-item label="服务类目">{{ detail.task.categoryName || '-' }}</a-descriptions-item>
          <a-descriptions-item label="下单模式">{{ detail.task.orderMode === 1 ? '一口价' : '竞价' }}</a-descriptions-item>
          <a-descriptions-item label="订单金额">{{ detail.task.totalAmount || '0.00' }}</a-descriptions-item>
          <a-descriptions-item label="报价人数">{{ detail.task.biddingCount || 0 }}</a-descriptions-item>
          <a-descriptions-item label="客户姓名">{{ detail.task.customerName || '-' }}</a-descriptions-item>
          <a-descriptions-item label="客户手机">{{ detail.task.customerPhone || '-' }}</a-descriptions-item>
          <a-descriptions-item label="服务地址" :span="2">{{ detail.task.serviceAddress || detail.task.addressDetail || '-' }}</a-descriptions-item>
          <a-descriptions-item label="期望日期">{{ detail.task.expectServeDate || '-' }}</a-descriptions-item>
          <a-descriptions-item label="期望时段">{{ formatTimeSlot(detail.task.expectServeTimeSlot || detail.task.expectedTime) }}</a-descriptions-item>
          <a-descriptions-item label="验收标准" :span="2">{{ detail.task.acceptanceCriteria || '-' }}</a-descriptions-item>
          <a-descriptions-item label="创建时间">{{ detail.task.createdAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="审核时间">{{ detail.task.auditAt || '-' }}</a-descriptions-item>
          <a-descriptions-item label="修改意见" :span="2">
            <span v-if="detail.task.revisionRemark" style="color:#fa541c;">{{ detail.task.revisionRemark }}</span>
            <span v-else>-</span>
          </a-descriptions-item>
        </a-descriptions>

        <div v-if="detail.task" style="margin-top: 16px; font-weight: 600;">商品与图片（共 {{ detail.itemGroups.length }} 组）</div>
        <a-table
          v-if="detail.task"
          :columns="itemGroupColumns"
          :data-source="detail.itemGroups"
          rowKey="id"
          size="small"
          :pagination="false"
          style="margin-top: 8px;"
        >
          <span slot="serviceItemNames" slot-scope="text, record">
            {{ formatServiceItemNames(record) }}
          </span>
          <span slot="images" slot-scope="text, record">
            <template v-if="Array.isArray(record.images) && record.images.length">
              <img
                v-for="(url, idx) in record.images"
                :key="idx"
                :src="url"
                style="width:48px;height:48px;object-fit:cover;margin-right:6px;border-radius:4px;cursor:pointer;"
                @click="handlePreviewImage(url)"
              />
            </template>
            <span v-else style="color:#bfbfbf;">-</span>
          </span>
        </a-table>

        <div v-if="detail.task" style="margin-top: 16px; font-weight: 600;">资质要求</div>
        <div v-if="detail.task" style="margin-top: 8px;">
          <template v-if="detail.requiredCerts.length">
            <a-tag v-for="cert in detail.requiredCerts" :key="cert.id || cert.certTypeId">
              {{ cert.certTypeNameSnapshot || cert.name || cert.certTypeId }}
            </a-tag>
          </template>
          <span v-else style="color:#bfbfbf;">无</span>
        </div>

        <div
          v-if="detail.task && detail.task.auditStatus === 0 && detail.task.status === 1"
          style="margin-top: 20px; text-align: right;"
        >
          <a-popconfirm title="确定通过该任务?" @confirm="handleApprove(detail.task.id, true)">
            <a-button type="primary">通过</a-button>
          </a-popconfirm>
          <a-button style="margin-left: 8px;" @click="handleOpenEdit(detail.task)">修改任务</a-button>
          <a-button style="margin-left: 8px;" @click="handleOpenRevision(detail.task)">未通过</a-button>
        </div>
      </a-spin>
    </a-modal>

    <a-modal
      v-model="imagePreviewVisible"
      title="图片预览"
      :footer="null"
      width="720px"
      @cancel="handleCloseImagePreview"
    >
      <img
        v-if="imagePreviewUrl"
        :src="imagePreviewUrl"
        style="display:block;max-width:100%;max-height:70vh;margin:0 auto;object-fit:contain;"
      />
    </a-modal>

    <a-modal
      v-model="editVisible"
      title="核审修改任务"
      :confirm-loading="editSubmitting"
      @ok="handleSubmitEdit"
      @cancel="handleCloseEdit"
    >
      <a-form-model :model="editForm" :label-col="{ span: 6 }" :wrapper-col="{ span: 16 }">
        <a-form-model-item label="任务标题"><a-input v-model="editForm.title" :max-length="150" /></a-form-model-item>
        <a-form-model-item label="服务地址"><a-input v-model="editForm.serviceAddress" :max-length="100" /></a-form-model-item>
        <a-form-model-item label="客户姓名"><a-input v-model="editForm.customerName" :max-length="50" /></a-form-model-item>
        <a-form-model-item label="客户电话"><a-input v-model="editForm.customerPhone" :max-length="11" /></a-form-model-item>
        <a-form-model-item label="服务日期"><a-date-picker v-model="editForm.expectServeDate" value-format="YYYY-MM-DD" style="width:100%" /></a-form-model-item>
        <a-form-model-item label="服务时段"><a-select v-model="editForm.expectServeTimeSlot"><a-select-option v-for="(label, value) in timeSlots" :key="value" :value="value">{{ label }}</a-select-option></a-select></a-form-model-item>
        <a-form-model-item label="任务金额">
          <a-input-number v-model="editForm.totalAmount" :min="0" :max="999999.99" :precision="2" disabled style="width:100%" />
          <div style="color:#999;line-height:20px;">金额由商品明细合计生成，审核时不单独修改</div>
        </a-form-model-item>
        <a-form-model-item label="竞价名额"><a-input-number v-model="editForm.quotaLimit" :min="1" :max="10" style="width:100%" /></a-form-model-item>
        <a-form-model-item label="验收要求"><a-textarea v-model="editForm.acceptanceCriteria" :rows="3" :max-length="500" /></a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      v-model="revisionVisible"
      title="未通过原因"
      :confirm-loading="revisionSubmitting"
      @ok="handleSubmitRevision"
      @cancel="handleCloseRevision"
    >
      <a-form-model
        ref="revisionForm"
        :model="revisionForm"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="修改意见">
          <a-textarea
            v-model="revisionForm.revisionRemark"
            :rows="5"
            placeholder="可填写修改意见，商家端将按该意见重新编辑任务"
          />
          <div
            :style="{
              textAlign: 'right',
              fontSize: '12px',
              color: (revisionForm.revisionRemark || '').trim().length > 500 ? 'red' : '#999'
            }"
          >
            {{ (revisionForm.revisionRemark || '').length }} / 500
          </div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import { getAppTaskList, getAppTaskDetail, auditApproveTask, auditEditTask, auditRevisionTask } from '@/api'

const TIME_SLOT_LABEL = {
  morning_8_12: '上午 08:00-12:00',
  noon_12_15: '中午 12:00-15:00',
  afternoon_15_18: '下午 15:00-18:00',
  evening_18_21: '晚上 18:00-21:00',
  all_day: '全天'
}

export default {
  data() {
    return {
      data: [],
      loading: false,
      query: {
        reviewProgress: 'PENDING',
        merchantId: undefined
      },
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0
      },
      detailVisible: false,
      detailLoading: false,
      detail: {
        task: null,
        itemGroups: [],
        requiredCerts: []
      },
      revisionVisible: false,
      editVisible: false,
      editSubmitting: false,
      editForm: {},
      revisionSubmitting: false,
      revisionForm: {
        id: null,
        revisionRemark: ''
      },
      imagePreviewVisible: false,
      imagePreviewUrl: '',
      columns: [
        { title: 'ID', dataIndex: 'id', width: 80 },
        { title: '商家ID', dataIndex: 'merchantId', width: 100 },
        { title: '任务标题', dataIndex: 'title' },
        { title: '类目', dataIndex: 'categoryName', width: 120 },
        { title: '服务地址', dataIndex: 'serviceAddress' },
        { title: '订单金额', dataIndex: 'totalAmount', width: 110 },
        {
          title: '审核进度',
          key: 'reviewProgress',
          width: 130,
          scopedSlots: { customRender: 'reviewProgress' }
        },
        {
          title: '修改意见',
          dataIndex: 'revisionRemark',
          width: 220,
          ellipsis: true,
          scopedSlots: { customRender: 'revisionRemark' }
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 170 },
        {
          title: '操作',
          key: 'action',
          width: 260,
          scopedSlots: { customRender: 'action' }
        }
      ],
      itemGroupColumns: [
        { title: '商品名称', dataIndex: 'productName', width: 150, ellipsis: true },
        { title: '数量', dataIndex: 'quantity', width: 70 },
        { title: '单价', dataIndex: 'unitPrice', width: 90 },
        {
          title: '服务项目',
          key: 'serviceItemNames',
          ellipsis: true,
          scopedSlots: { customRender: 'serviceItemNames' }
        },
        { title: '备注', dataIndex: 'remark', ellipsis: true },
        {
          title: '图片',
          key: 'images',
          width: 240,
          scopedSlots: { customRender: 'images' }
        }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    handleOpenEdit(record) {
      const task = (this.detail.task && this.detail.task.id === record.id) ? this.detail.task : record
      this.editForm = {
        id: task.id, title: task.title || '', serviceAddress: task.serviceAddress || task.addressDetail || '',
        customerName: task.customerName || '', customerPhone: task.customerPhone || '',
        expectServeDate: task.expectServeDate || null, expectServeTimeSlot: task.expectServeTimeSlot || 'all_day',
        totalAmount: task.totalAmount === undefined || task.totalAmount === null ? 0 : Number(task.totalAmount),
        quotaLimit: Number(task.orderMode) === 2 ? (task.quotaLimit || 1) : null, acceptanceCriteria: task.acceptanceCriteria || ''
      }
      this.editVisible = true
    },
    handleCloseEdit() { this.editVisible = false; this.editForm = {} },
    handleSubmitEdit() {
      const form = this.editForm
      if (!form.title || !form.serviceAddress || !form.customerName || !/^1\d{10}$/.test(form.customerPhone || '')) {
        this.$message.error('请完整填写标题、地址、客户姓名和手机号')
        return
      }
      this.editSubmitting = true
      const payload = Object.assign({}, form)
      delete payload.id
      auditEditTask(form.id, payload)
        .then(() => {
          this.$message.success('任务已修改')
          this.handleCloseEdit()
          if (this.detailVisible) this.handleOpenDetail({ id: form.id })
          this.loadData()
        })
        .finally(() => { this.editSubmitting = false })
    },
    buildQueryParams() {
      const params = {
        reviewReady: true,
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        merchantId: this.query.merchantId || undefined
      }
      if (this.query.reviewProgress === 'PENDING') {
        params.auditStatus = 0
        params.status = 1
      } else if (this.query.reviewProgress === 'APPROVED') {
        params.auditStatus = 1
      } else if (this.query.reviewProgress === 'NEED_REVISION') {
        params.auditStatus = 0
        params.status = 2
      }
      return params
    },
    loadData() {
      this.loading = true
      getAppTaskList(this.buildQueryParams())
        .then(res => {
          const payload = (res && res.data) || {}
          if (payload.records) {
            this.data = payload.records
            this.pagination.total = payload.total || 0
          } else if (payload.list) {
            this.data = payload.list
            this.pagination.total = payload.total || 0
          } else if (Array.isArray(payload)) {
            this.data = payload
            this.pagination.total = payload.length
          } else {
            this.data = []
            this.pagination.total = 0
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },
    handleReset() {
      this.query = {
        reviewProgress: 'PENDING',
        merchantId: undefined
      }
      this.pagination.current = 1
      this.loadData()
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current
      this.pagination.pageSize = pagination.pageSize
      this.loadData()
    },
    handleOpenDetail(record) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = { task: null, itemGroups: [], requiredCerts: [] }
      getAppTaskDetail(record.id)
        .then(res => {
          const payload = (res && res.data) || {}
          this.detail = {
            task: payload.task || null,
            itemGroups: this.normalizeItemGroups(payload.itemGroups),
            requiredCerts: Array.isArray(payload.requiredCerts) ? payload.requiredCerts : []
          }
        })
        .catch(err => {
          this.$message.error(err && err.message ? err.message : '加载详情失败')
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    handleCloseDetail() {
      this.detailVisible = false
      this.detail = { task: null, itemGroups: [], requiredCerts: [] }
    },
    normalizeItemGroups(itemGroups) {
      if (!Array.isArray(itemGroups)) {
        return []
      }
      return itemGroups.map((item, index) => {
        const group = item.group || item || {}
        const images = Array.isArray(item.images)
          ? item.images.map(img => img.imageUrl || img.url || img).filter(Boolean)
          : []
        const serviceItems = Array.isArray(item.serviceItems) ? item.serviceItems : []
        return {
          id: group.id || index,
          productName: group.productNameSnapshot || group.productName || '-',
          quantity: group.quantity,
          unitPrice: group.unitPrice,
          remark: group.remark || '',
          serviceItems,
          images
        }
      })
    },
    formatServiceItemNames(record) {
      const serviceItems = record && record.serviceItems
      if (!Array.isArray(serviceItems) || serviceItems.length === 0) {
        return '-'
      }
      const names = serviceItems
        .map(item => item.serviceItemNameSnapshot || item.name)
        .filter(Boolean)
      return names.length ? names.join('、') : '-'
    },
    handlePreviewImage(url) {
      if (url) {
        this.imagePreviewUrl = url
        this.imagePreviewVisible = true
      }
    },
    handleCloseImagePreview() {
      this.imagePreviewVisible = false
      this.imagePreviewUrl = ''
    },
    formatTimeSlot(value) {
      if (!value) {
        return '-'
      }
      return TIME_SLOT_LABEL[value] || value
    },
    handleApprove(id, closeDetail = false) {
      auditApproveTask(id).then(() => {
        this.$message.success('任务审核通过')
        if (closeDetail) {
          this.handleCloseDetail()
        }
        this.loadData()
      })
    },
    handleOpenRevision(record) {
      this.revisionForm = {
        id: record.id,
        revisionRemark: ''
      }
      this.revisionVisible = true
    },
    handleCloseRevision() {
      this.revisionVisible = false
      this.revisionForm = { id: null, revisionRemark: '' }
    },
    handleSubmitRevision() {
      const remark = (this.revisionForm.revisionRemark || '').trim()
      if (remark.length > 500) {
        this.$message.error('修改意见不能超过 500 字')
        return
      }
      this.revisionSubmitting = true
      auditRevisionTask(this.revisionForm.id, { revisionRemark: remark || null })
        .then(() => {
          this.$message.success('已标记为未通过')
          this.revisionVisible = false
          if (this.detailVisible) {
            this.handleCloseDetail()
          }
          this.revisionForm = { id: null, revisionRemark: '' }
          this.loadData()
        })
        .finally(() => {
          this.revisionSubmitting = false
        })
    }
  }
}
</script>
